import { useState } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { ThemeToggle } from "@/components/custom/theme/theme-toggle"
import { Hexagon, LogOut, User, Bell, Menu, Check } from "lucide-react"
import { useCurrency } from "@/context/currency/currency-context"
import { useAuth } from "@/context/auth/auth-context"
import { useSaveCurrency } from "@/hooks/use-save-currency"
import { CURRENCIES } from "@/lib/currency-data"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"

interface NavbarProps {
	onMenuClick?: () => void
}

export function Navbar({ onMenuClick }: NavbarProps) {
	const { currency } = useCurrency()
	const { saveCurrency } = useSaveCurrency()
	const { user, logout, isAuthenticated, isLoading } = useAuth()
	const navigate = useNavigate()
	const location = useLocation()
	const [currencyOpen, setCurrencyOpen] = useState(false)
	const [search, setSearch] = useState("")

	const filtered = Object.values(CURRENCIES).filter(
		(c) =>
			search === "" ||
			c.code.includes(search.toUpperCase()) ||
			c.name.toLowerCase().includes(search.toLowerCase()),
	)

	const handleLogout = () => {
		logout()
		navigate("/")
	}

	return (
		<header className="sticky top-0 z-50 w-full border-b border-sky-900/10 dark:border-sky-100/10 bg-white/40 dark:bg-black/40 backdrop-blur-md">
			<div className="container mx-auto px-4 h-16 flex items-center justify-between">
				{/* LOGO + MOBILE HAMBURGER */}
				<div className="flex items-center gap-2">
					{isAuthenticated && (
						<Button
							variant="ghost"
							size="icon"
							className="md:hidden text-sky-900 dark:text-sky-100"
							onClick={onMenuClick}
							aria-label="Open navigation menu"
						>
							<Menu className="h-5 w-5" />
						</Button>
					)}
					<Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center gap-2 group">
						<div className="relative w-8 h-8 flex items-center justify-center bg-sky-500 rounded-lg transform group-hover:rotate-12 transition-transform shadow-lg shadow-sky-500/30">
							<Hexagon className="w-5 h-5 text-white fill-white" />
						</div>
						<span className="font-extrabold text-xl tracking-tight text-sky-950 dark:text-sky-50">
							Bee<span className="text-sky-600 dark:text-sky-400">Div</span>
						</span>
					</Link>
				</div>

				{/* ACTIONS */}
				<div className="flex items-center gap-2 md:gap-4">
					<Popover open={currencyOpen} onOpenChange={(open) => { setCurrencyOpen(open); if (!open) setSearch("") }}>
						<PopoverTrigger asChild>
							<Button
								variant="ghost"
								size="sm"
								className="font-semibold text-sky-900 dark:text-sky-100 gap-1.5 px-2"
							>
								<span>{currency.flag}</span>
								<span>{currency.code}</span>
							</Button>
						</PopoverTrigger>
						<PopoverContent align="end" className="w-[340px] p-0">
							<div className="p-2 border-b border-sky-900/10 dark:border-sky-100/10">
								<input
									placeholder="Search currencies..."
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									className="w-full text-sm px-2 py-1.5 rounded-md bg-sky-50 dark:bg-sky-950/30 outline-none border border-sky-200 dark:border-sky-800 placeholder:text-muted-foreground"
									autoFocus
								/>
							</div>
							<div className="grid grid-cols-2 gap-0.5 p-2 max-h-72 overflow-y-auto">
								{filtered.map((c) => (
									<button
										key={c.code}
										onClick={() => {
											saveCurrency(c.code)
											setCurrencyOpen(false)
											setSearch("")
										}}
										className={cn(
											"flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-left hover:bg-sky-50 dark:hover:bg-sky-900/20 transition-colors w-full",
											c.code === currency.code && "bg-sky-100 dark:bg-sky-900/30 font-semibold",
										)}
									>
										<span className="text-base leading-none">{c.flag}</span>
										<span className="font-mono text-xs text-muted-foreground w-8 shrink-0">{c.code}</span>
										<span className="truncate text-xs">{c.name}</span>
										{c.code === currency.code && (
											<Check className="h-3 w-3 ml-auto shrink-0 text-sky-600 dark:text-sky-400" />
										)}
									</button>
								))}
								{filtered.length === 0 && (
									<p className="col-span-2 text-center text-sm text-muted-foreground py-4">
										No currencies found
									</p>
								)}
							</div>
						</PopoverContent>
					</Popover>

					<ThemeToggle />

					{isLoading ? (
						<div className="w-[100px] h-8" />
					) : isAuthenticated ? (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="ghost"
									className="flex items-center gap-2 px-2 hover:bg-sky-100/50 dark:hover:bg-sky-900/30"
								>
									<span className="hidden md:inline font-medium text-sky-900 dark:text-sky-100">
										Hi, {user?.first_name}
									</span>
									<div className="h-8 w-8 bg-sky-100 dark:bg-sky-900 rounded-full flex items-center justify-center border border-sky-200 dark:border-sky-800">
										<User className="h-4 w-4 text-sky-700 dark:text-sky-300" />
									</div>
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end" className="w-48">
								<DropdownMenuItem asChild>
									<Link
										to="/notifications"
										className="cursor-pointer font-medium flex items-center gap-2"
									>
										<Bell className="h-4 w-4" />
										Email Preferences
									</Link>
								</DropdownMenuItem>
								<DropdownMenuSeparator />
								<DropdownMenuItem
									onClick={handleLogout}
									className="text-red-600 focus:text-red-600 cursor-pointer focus:bg-red-50 dark:focus:bg-red-950/30"
								>
									<LogOut className="mr-2 h-4 w-4" />
									Log out
								</DropdownMenuItem>
							</DropdownMenuContent>
						</DropdownMenu>
					) : (
						<div className="flex items-center gap-2">
							<Link to="/login" state={location.pathname !== "/" ? { from: location } : undefined}>
								<Button
									variant="ghost"
									size="sm"
									className="hidden sm:inline-flex text-sky-900 dark:text-sky-100 font-medium"
								>
									Login
								</Button>
							</Link>
							<Link to="/signup" state={location.pathname !== "/" ? { from: location } : undefined}>
								<Button
									size="sm"
									className="bg-sky-600 hover:bg-sky-700 text-white font-bold rounded-full px-5 shadow-md shadow-sky-600/20 transition-all hover:scale-105"
								>
									Get Started
								</Button>
							</Link>
						</div>
					)}
				</div>
			</div>
		</header>
	)
}
